import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Button, notification, Space } from 'antd'
import { Trans, Translation } from 'react-i18next'

import { BULK_UPDATES_QUERY_PREFIX } from 'features/BulkUpdate/bulkUpdatesQueries'
import { navigate } from 'routing/RoutingService'
import { uploadPageUrl } from 'routing/urls'

import { TransObj } from './translation'

const top = 50
const duration = 5

export interface UpdateNotificationOptions {
    name: string
    count?: number
    dryRun?: boolean
}

function showUpdateNotification({
    name,
    count,
    dryRun = false,
}: UpdateNotificationOptions): void {
    notification.config({
        top,
        duration,
    })
    let description = <span />

    if (dryRun) {
        if (count) {
            description = (
                <Trans i18nKey="table:BulkActions.pastPageSubmit.dryRun.successDesc">
                    <span>
                        Dry run update for{' '}
                        <strong>
                            {{ total: count } as TransObj}{' '}
                            {{ name } as TransObj}(s)
                        </strong>
                        .
                    </span>
                </Trans>
            )
        } else {
            description = (
                <Trans i18nKey="table:BulkActions.pastPageSubmit.dryRun.noCount.successDesc">
                    <span>
                        Dry run update for{' '}
                        <strong>{{ name } as TransObj}(s)</strong>.
                    </span>
                </Trans>
            )
        }
    } else if (count) {
        description = (
            <Trans i18nKey="table:BulkActions.pastPageSubmit.bulkUpdate.successDesc">
                <span>
                    Bulk updating{' '}
                    <strong>
                        {{ total: count } as TransObj} {{ name } as TransObj}(s)
                    </strong>
                    .
                </span>
            </Trans>
        )
    } else {
        description = (
            <Trans i18nKey="table:BulkActions.pastPageSubmit.bulkUpdate.noCount.successDesc">
                <span>
                    Bulk updating <strong>{{ name } as TransObj}(s)</strong>.
                </span>
            </Trans>
        )
    }
    const btn = (
        <Space>
            <Button
                type="link"
                size="small"
                onClick={() => {
                    notification.destroy()
                    navigate(uploadPageUrl)
                }}
            >
                <Translation>
                    {(t) => t('common:viewUpdates', 'View Bulk Updates')}
                </Translation>
            </Button>
        </Space>
    )
    notification.success({
        message: dryRun ? (
            <Translation>
                {(t) =>
                    t(
                        'table:BulkActions.pastPageSubmit.dryRun.successMsg',
                        'Dry Run Started'
                    )
                }
            </Translation>
        ) : (
            <Translation>
                {(t) =>
                    t(
                        'table:BulkActions.pastPageSubmit.bulkUpdate.successMsg',
                        'Bulk Update Started'
                    )
                }
            </Translation>
        ),
        description,
        btn,
    })
}

export function useGenerateUpdateNotification(): (
    options: UpdateNotificationOptions
) => void {
    const queryClient = useQueryClient()
    return useCallback(
        (options: UpdateNotificationOptions) => {
            queryClient.invalidateQueries({
                queryKey: [BULK_UPDATES_QUERY_PREFIX],
            })
            showUpdateNotification(options)
        },
        [queryClient]
    )
}

export const generateCodeSentNotification = (phoneNumber: string): void => {
    notification.config({
        top,
        duration,
        placement: 'topRight',
    })
    notification.success({
        message: (
            <Translation>
                {(t) =>
                    t(
                        'account:LoginForm.smsCodeSentNotification.successMsg',
                        'Code Sent'
                    )
                }
            </Translation>
        ),
        description: (
            <Translation>
                {(t) =>
                    t(
                        'account:LoginForm.smsCodeSentNotification.successDesc',
                        `Your code was sent to: ${phoneNumber}`
                    )
                }
            </Translation>
        ),
    })
}
